// aos.js — Single file ES6 module version

// ---------------- debounce ----------------
function debounce(func, wait = 0, options = {}) {
  let timeout, lastCallTime, lastInvokeTime = 0;
  let leading = false;
  let trailing = true;
  let maxWait;
  let result;

  if (typeof func !== 'function') {
    throw new TypeError('Expected a function');
  }

  if (options) {
    leading = !!options.leading;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
    maxWait = options.maxWait;
  }

  function invoke(time) {
    const args = lastArgs;
    const thisArg = lastThis;
    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  let lastArgs, lastThis;

  function startTimer(pendingFunc, wait) {
    return setTimeout(pendingFunc, wait);
  }

  function shouldInvoke(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    return (
      lastCallTime === undefined ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      (maxWait && timeSinceLastInvoke >= maxWait)
    );
  }

  function trailingEdge(time) {
    timeout = undefined;
    if (trailing && lastArgs) {
      return invoke(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timeout = startTimer(timerExpired, wait);
  }

  function debounced(...args) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (!timeout) {
        if (leading) {
          return invoke(time);
        }
        timeout = startTimer(timerExpired, wait);
      }
    }

    if (!timeout) {
      timeout = startTimer(timerExpired, wait);
    }

    return result;
  }

  debounced.cancel = () => {
    clearTimeout(timeout);
    timeout = lastArgs = lastThis = undefined;
  };

  return debounced;
}

// ---------------- throttle ----------------
function throttle(func, wait, options = {}) {
  return debounce(func, wait, {
    leading: options.leading !== false,
    trailing: options.trailing !== false,
    maxWait: wait
  });
}

// ---------------- getOffset ----------------
function getOffset(el) {
  let top = 0;
  let left = 0;

  while (el && !isNaN(el.offsetLeft) && !isNaN(el.offsetTop)) {
    top += el.offsetTop - (el.tagName !== 'BODY' ? el.scrollTop : 0);
    left += el.offsetLeft - (el.tagName !== 'BODY' ? el.scrollLeft : 0);
    el = el.offsetParent;
  }

  return { top, left };
}

// ---------------- getPosition ----------------
function getPosition(el, defaultOffset) {
  let offset = 0;
  let element = el;

  const attrOffset = el.getAttribute('data-aos-offset');

  if (attrOffset && !isNaN(attrOffset)) {
    offset = parseInt(attrOffset);
  } else if (!isNaN(defaultOffset)) {
    offset = defaultOffset;
  }

  const position = getOffset(element).top;

  return position + offset;
}

// ---------------- getElements ----------------
function getElements() {
  const nodes = document.querySelectorAll('[data-aos]');
  return Array.from(nodes).map(node => ({ node }));
}

// ---------------- prepare ----------------
function prepare(elements, options) {
  return elements.map(el => {
    el.node.classList.add('aos-init');
    el.position = getPosition(el.node, options.offset);
    return el;
  });
}

// ---------------- applyClasses ----------------
function applyClasses(elements, once) {
  const scrollTop = window.pageYOffset;
  const windowHeight = window.innerHeight;

  elements.forEach(el => {
    if (windowHeight + scrollTop > el.position) {
      el.node.classList.add('aos-animate');
    } else if (!once) {
      el.node.classList.remove('aos-animate');
    }
  });
}

// ---------------- Mutation Observer ----------------
function observe(selector, callback) {
  const observer = new MutationObserver(() => {
    document.querySelectorAll(selector).forEach(el => {
      if (!el._aosObserved) {
        el._aosObserved = true;
        callback(el);
      }
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    removedNodes: true
  });

  callback();
}

// ---------------- device detection ----------------
const device = {
  mobile() {
    return /android|iphone|ipad|mobile/i.test(navigator.userAgent);
  },
  phone() {
    return /android|iphone|mobile/i.test(navigator.userAgent);
  },
  tablet() {
    return /ipad|tablet/i.test(navigator.userAgent);
  }
};

// ---------------- Main AOS ----------------
let elements = [];
let initialized = false;

const isOldIE = document.all && !window.atob;

let options = {
  offset: 120,
  delay: 0,
  easing: 'ease',
  duration: 400,
  disable: false,
  once: false,
  startEvent: 'DOMContentLoaded'
};

function refresh(initialize = false) {
  if (initialize) initialized = true;
  if (!initialized) return;

  elements = prepare(elements, options);
  applyClasses(elements, options.once);

  return elements;
}

function refreshHard() {
  elements = getElements();
  refresh();
}

function disableAnimations() {
  elements.forEach(el => {
    el.node.removeAttribute('data-aos');
  });
}

function isDisabled(disableOption) {
  return (
    disableOption === true ||
    (disableOption === 'mobile' && device.mobile()) ||
    (disableOption === 'phone' && device.phone()) ||
    (disableOption === 'tablet' && device.tablet()) ||
    (typeof disableOption === 'function' && disableOption())
  );
}

function init(userOptions = {}) {
  options = { ...options, ...userOptions };

  elements = getElements();

  if (isDisabled(options.disable) || isOldIE) {
    disableAnimations();
    return;
  }

  document.body.setAttribute('data-aos-easing', options.easing);
  document.body.setAttribute('data-aos-duration', options.duration);
  document.body.setAttribute('data-aos-delay', options.delay);

  if (
    options.startEvent === 'DOMContentLoaded' &&
    ['complete', 'interactive'].includes(document.readyState)
  ) {
    refresh(true);
  } else {
    document.addEventListener(options.startEvent, () => refresh(true));
  }

  window.addEventListener('resize', debounce(refresh, 50));
  window.addEventListener('orientationchange', debounce(refresh, 50));

  window.addEventListener(
    'scroll',
    throttle(() => applyClasses(elements, options.once), 99)
  );

  observe('[data-aos]', refreshHard);

  return elements;
}

// ---------------- exports ----------------
export default {
  init,
  refresh,
  refreshHard
};

export { init, refresh, refreshHard };